import { ArrowPathIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Callout } from '@/components/core/Callout';
import { PanelHeader } from '@/components/core/Panel';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import {
    isPreconditionFailed,
    readUpdatedAt,
    unfinishedExportsCutoff,
    useCurrentOperationTexts,
    useDeleteOldUnfinishedExports,
    useRecalculateOperation,
    useRecalculateStatistics,
    useReindexItems,
    useRevalidateItems,
} from './admin-settings.queries';
import { formatDateToSimpleString } from './formatDateToSimpleString';

interface ForceConfirm {
    operationName: string;
    lastUpdatedAt: string;
}

const REFRESH_NAME = 'Refresh';
// Shared width so every action stays the same size in longer languages (German).
const ACTION_BUTTON_CLASS = 'tw-w-96 tw-max-w-full tw-shrink-0 tw-justify-center';

function operationIsInProgress(operations: string[]): boolean {
    return operations.toString().match(/\w/g) != null;
}

export default function AdminSettingsPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { data, refetch } = useCurrentOperationTexts({ retry: false });
    const operations = Array.isArray(data) ? data : [];
    const inProgress = operationIsInProgress(operations);

    const revalidateItems = useRevalidateItems();
    const recalculateStatistics = useRecalculateStatistics();
    const reindexItems = useReindexItems();
    const deleteExports = useDeleteOldUnfinishedExports();
    const recalculateOperation = useRecalculateOperation();

    const [forceConfirm, setForceConfirm] = useState<ForceConfirm | null>(null);
    const bannerRefreshTimers = useRef<number[]>([]);

    useEffect(() => {
        const timers = bannerRefreshTimers.current;
        return () => {
            timers.forEach((timerId) => window.clearTimeout(timerId));
        };
    }, []);

    const scheduleBannerRefresh = () => {
        const timerId = window.setTimeout(() => {
            void refetch();
        }, 500);
        bannerRefreshTimers.current.push(timerId);
    };

    const runQuiet = async (task: () => Promise<unknown>) => {
        scheduleBannerRefresh();
        try {
            await task();
        } catch {
            // Revalidate and reindex have no error callback in the Angular controller.
        }
    };

    const startRecalculate = async (force = false) => {
        scheduleBannerRefresh();
        try {
            await recalculateStatistics.mutateAsync({ force });
            toast.info('ADMINISTRATION.RECALCULATION_IN_PROGRESS');
        } catch (error) {
            if (!force && isPreconditionFailed(error)) {
                await offerForceRestart();
            }
        }
    };

    const offerForceRestart = async () => {
        try {
            const operation = await recalculateOperation.mutateAsync();
            const updatedAt = readUpdatedAt(operation);
            if (updatedAt == null) {
                await startRecalculate(true);
                return;
            }

            setForceConfirm({
                operationName: t('ADMINISTRATION.RECALCULATE_STATISTICS'),
                lastUpdatedAt: formatDateToSimpleString(updatedAt, t),
            });
        } catch {
            // The Angular success callback is the only path that opens the dialog.
        }
    };

    const deleteOldUnfinishedExports = async () => {
        toast.info('OPERATION.DELETE_OLD_UNFINISHED_EXPORTS.STARTED');
        try {
            await deleteExports.mutateAsync({ before: unfinishedExportsCutoff() });
            toast.success('OPERATION.DELETE_OLD_UNFINISHED_EXPORTS.FINISHED');
        } catch {
            // The Angular delete call has no error callback.
        }
    };

    const actions = [
        {
            titleKey: 'ADMINISTRATION.REVALIDATE_ALL_ITEMS',
            textKey: 'ADMINISTRATION.REVALIDATE_TEXT',
            run: () => runQuiet(() => revalidateItems.mutateAsync({})),
        },
        {
            titleKey: 'ADMINISTRATION.RECALCULATE_STATISTICS',
            textKey: 'ADMINISTRATION.RECALCULATE_TEXT',
            run: () => startRecalculate(false),
        },
        {
            titleKey: 'ADMINISTRATION.REVALIDATE_DIRTY_ITEMS',
            textKey: 'ADMINISTRATION.REVALIDATE_DIRTY_TEXT',
            run: () => runQuiet(() => revalidateItems.mutateAsync({ onlyDirty: true })),
        },
        {
            titleKey: 'ADMINISTRATION.REINDEX_ALL_ITEMS',
            textKey: 'ADMINISTRATION.REINDEX_ALL_ITEMS.TEXT',
            run: () => runQuiet(() => reindexItems.mutateAsync()),
        },
        {
            titleKey: 'ADMINISTRATION.DELETE_OLD_UNFINISHED_EXPORTS',
            textKey: 'ADMINISTRATION.DELETE_OLD_UNFINISHED_EXPORTS.TEXT',
            run: deleteOldUnfinishedExports,
        },
    ];

    return (
        <div className="tw-flex tw-flex-col">
            <PanelHeader
                className="tw-shrink-0 !tw-pb-4"
                icon={Cog6ToothIcon}
                title={t('ADMINISTRATION.SETTINGS')}
            />
            <div className="tw-rounded-lg tw-bg-fill-primary tw-p-4">
                <div className="tw-flex tw-flex-col tw-gap-4">
                    {inProgress && (
                        <div className="tw-mx-auto tw-w-full tw-max-w-xl">
                            <Callout variant="warning" canBeClosed={false}>
                                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                                        {operations.map((operation, index) => (
                                            <p
                                                key={`${operation}-${index}`}
                                                className="tw-m-0 tw-text-center tw-text-t-sm lg:tw-text-t-base"
                                            >
                                                {t(operation)}
                                            </p>
                                        ))}
                                    </div>
                                    <ButtonPanel
                                        icon={ArrowPathIcon}
                                        isDisabled={false}
                                        aria-label={REFRESH_NAME}
                                        tooltip={REFRESH_NAME}
                                        onClick={() => {
                                            void refetch();
                                        }}
                                    />
                                </div>
                            </Callout>
                        </div>
                    )}
                    {actions.map((action, index) => (
                        <div key={action.titleKey} className="tw-flex tw-flex-col tw-gap-4">
                            {index > 0 && (
                                <hr className="tw-m-0 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-tertiary" />
                            )}
                            <section className="tw-flex tw-flex-col tw-gap-2">
                                <h2 className="tw-m-0 tw-text-t-sm tw-font-bold lg:tw-text-t-base">
                                    {t(action.titleKey)}
                                </h2>
                                <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4">
                                    <p className="tw-m-0 tw-min-w-0 tw-flex-1 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                        {t(action.textKey)}
                                    </p>
                                    <Button
                                        variant="default"
                                        className={ACTION_BUTTON_CLASS}
                                        disabled={inProgress}
                                        onClick={() => {
                                            void action.run();
                                        }}
                                    >
                                        {t(action.titleKey)}
                                    </Button>
                                </div>
                            </section>
                        </div>
                    ))}
                </div>
            </div>
            <ConfirmationModal
                show={forceConfirm != null}
                title="ADMINISTRATION.OPERATION_IN_PROGRESS_HEADER"
                onSubmit={() => {
                    setForceConfirm(null);
                    void startRecalculate(true);
                }}
                onClose={() => setForceConfirm(null)}
            >
                {forceConfirm
                    ? t('ADMINISTRATION.FORCE_OPERATION_EXECUTION', {
                        operationName: forceConfirm.operationName,
                        lastUpdatedAt: forceConfirm.lastUpdatedAt,
                    })
                    : null}
            </ConfirmationModal>
        </div>
    );
}
